import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import healthApi from '@/api/healthApi';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  ArrowLeft, Save, Plus, X, Pencil,
  Heart, AlertTriangle, Pill,
  Bell, Mail, Lock, Globe2,
  Activity, CalendarClock, ShieldCheck,
  Sparkles, Flame, BarChart3, MessageSquare, Bug, HelpCircle, Stethoscope,
} from 'lucide-react';
import { toast } from 'sonner';
import ChangePassword from '@/components/ChangePassword';
import LanguageSelector from '@/components/LanguageSelector';
import FeedbackModal from '@/components/FeedbackModal';
import ReportProblemModal from '@/components/ReportProblemModal';
import ClinicianShareModal from '@/components/ClinicianShareModal';
import CoachContinuityToggle from '@/components/CoachContinuityToggle';
import AppFooter from '@/components/AppFooter';
import { WellnessPendingBadge } from '@/components/WellnessPendingBadge';

const PROFILE_FIELDS = [
  'name', 'date_of_birth', 'gender', 'height', 'weight',
  'medical_conditions', 'allergies', 'medications',
];

// Shared input className — touch-friendly h-12, soft focus ring, calm shadow.
// Used everywhere profile fields render so inputs feel consistent.
const FIELD_CLASS =
  'h-12 rounded-2xl bg-white border-gray-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)] ' +
  'focus-visible:ring-2 focus-visible:ring-emerald-200 focus-visible:border-emerald-300 ' +
  'disabled:bg-gray-50 disabled:text-gray-500 motion-safe:transition-colors';

// ============================================================================
// MICRO-COACH SIGNAL MEMORY (localStorage, per-user)
// ----------------------------------------------------------------------------
// Quiet personalization for the "Next on your agenda" coach line. We record
// {shown, engaged, last_shown_iso} per suggestion, scoped to a single user.
// Used ONLY to:
//   - cool down a suggestion shown in the last 6 hours
//   - lift suggestions the user has tapped (engagement signal)
//   - lower suggestions repeatedly shown without engagement (soft fade)
// We never display scores, never gamify, and the deterministic library is
// the fallback when everything's in cooldown.
// ============================================================================
const COACH_STORAGE_KEY = (userId) => `hl_coach_signals_v1_${userId || 'anon'}`;
const COACH_COOLDOWN_MS = 6 * 60 * 60 * 1000; // 6 hours

const loadCoachSignals = (userId) => {
  try {
    const raw = window.localStorage?.getItem(COACH_STORAGE_KEY(userId));
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
};

const saveCoachSignals = (userId, signals) => {
  try {
    window.localStorage?.setItem(COACH_STORAGE_KEY(userId), JSON.stringify(signals));
  } catch { /* storage may be unavailable — fail quietly */ }
};

const recordCoachShown = (userId, label) => {
  const s = loadCoachSignals(userId);
  const cur = s[label] || { shown: 0, engaged: 0, last_shown: null };
  s[label] = {
    shown: cur.shown + 1,
    engaged: cur.engaged,
    last_shown: new Date().toISOString(),
  };
  saveCoachSignals(userId, s);
};

const recordCoachEngaged = (userId, label) => {
  const s = loadCoachSignals(userId);
  const cur = s[label] || { shown: 0, engaged: 0, last_shown: null };
  s[label] = {
    shown: cur.shown,
    engaged: cur.engaged + 1,
    last_shown: cur.last_shown,
  };
  saveCoachSignals(userId, s);
};

// Score a candidate: engagement raises, repeated-no-engagement lowers,
// recent shows are filtered out entirely (cooldown). Range typically ~ -3..+6.
const scoreCoach = (signals, label) => {
  const s = signals[label];
  if (!s) return 0; // never shown → neutral baseline
  if (s.last_shown) {
    const since = Date.now() - new Date(s.last_shown).getTime();
    if (since < COACH_COOLDOWN_MS) return -Infinity; // hard cooldown
  }
  const noEngage = Math.max(0, s.shown - s.engaged);
  // Engagement weighted 2x, fade penalty capped softly at 3
  return s.engaged * 2 - Math.min(3, noEngage);
};

// ============================================================================
// SESSION-STABLE CHIP SELECTION
// ----------------------------------------------------------------------------
// Goal: the chip's chosen label stays stable across renders, refreshes, and
// other tabs UNTIL the underlying context materially changes — or 4 hours
// of inactivity pass. This avoids slot-machine flicker, supports calm
// ambient guidance, and keeps the experience consistent across a session.
//
// We cache `{label, fingerprint, picked_at, mode}` in localStorage (so it
// survives reloads & propagates across tabs via the storage event). The
// fingerprint encodes the contextual variables that SHOULD invalidate a
// pick: which reminder is winning, whether the user has checked in, and
// the current time-of-day bucket for coach mode.
// ============================================================================
const COACH_SESSION_KEY = (userId) => `hl_coach_session_v1_${userId || 'anon'}`;
const COACH_SESSION_TTL_MS = 4 * 60 * 60 * 1000; // 4 hours

const loadCoachSession = (userId) => {
  try {
    const raw = window.localStorage?.getItem(COACH_SESSION_KEY(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.picked_at) return null;
    const age = Date.now() - new Date(parsed.picked_at).getTime();
    if (age > COACH_SESSION_TTL_MS) return null; // expired
    return parsed;
  } catch { return null; }
};

const saveCoachSession = (userId, payload) => {
  try {
    window.localStorage?.setItem(
      COACH_SESSION_KEY(userId),
      JSON.stringify({ ...payload, picked_at: new Date().toISOString() }),
    );
  } catch { /* storage unavailable */ }
};

const clearCoachSession = (userId) => {
  try {
    window.localStorage?.removeItem(COACH_SESSION_KEY(userId));
  } catch { /* empty */ }
};

// ============================================================================
// POST-ENGAGEMENT BREATHING ROOM
// ----------------------------------------------------------------------------
// After the user taps a coach suggestion, we deliberately step BACK for
// ~45 minutes. The chip shows a quiet acknowledgment instead of another
// "optimized" pick — interaction matters more than impressions, and we
// want to preserve emotional breathing room rather than feel like a
// recommender system that immediately serves the next thing.
// ============================================================================
const COACH_POST_ENGAGE_KEY = (userId) => `hl_coach_post_engage_v1_${userId || 'anon'}`;
const COACH_POST_ENGAGE_MS = 45 * 60 * 1000; // 45 minutes

const recordEngagementMoment = (userId) => {
  try {
    window.localStorage?.setItem(
      COACH_POST_ENGAGE_KEY(userId),
      new Date().toISOString(),
    );
  } catch { /* empty */ }
};

const isPostEngagementWindow = (userId) => {
  try {
    const raw = window.localStorage?.getItem(COACH_POST_ENGAGE_KEY(userId));
    if (!raw) return false;
    const age = Date.now() - new Date(raw).getTime();
    return age >= 0 && age < COACH_POST_ENGAGE_MS;
  } catch { return false; }
};

// MAX of shown/engaged (so neither device's history is lost), and the
// LATER `last_shown` (so cooldown is respected globally). This is
// idempotent and order-independent — safe to call at any time.
const mergeCoachSignals = (userId, serverSignals) => {
  if (!serverSignals || typeof serverSignals !== 'object') return;
  const local = loadCoachSignals(userId);
  const merged = { ...local };
  for (const [label, s] of Object.entries(serverSignals)) {
    if (!s) continue;
    const cur = merged[label] || { shown: 0, engaged: 0, last_shown: null };
    const a = cur.last_shown ? new Date(cur.last_shown).getTime() : 0;
    const b = s.last_shown ? new Date(s.last_shown).getTime() : 0;
    merged[label] = {
      shown: Math.max(cur.shown || 0, s.shown || 0),
      engaged: Math.max(cur.engaged || 0, s.engaged || 0),
      last_shown: a >= b ? cur.last_shown : s.last_shown,
    };
  }
  saveCoachSignals(userId, merged);
};

// Compute % completion across the 8 fields above. Arrays count as complete
// when they have at least one entry. Empty strings / null / 0 don't count.
const computeCompletion = (data) => {
  let filled = 0;
  for (const k of PROFILE_FIELDS) {
    const v = data?.[k];
    if (Array.isArray(v) ? v.length > 0 : v !== '' && v !== null && v !== undefined) {
      filled += 1;
    }
  }
  return Math.round((filled / PROFILE_FIELDS.length) * 100);
};

const SectionCard = ({ id, eyebrow, title, icon: Icon, children, accent = 'emerald' }) => {
  const accents = {
    emerald: { tile: 'from-emerald-100 to-teal-100 text-emerald-700', card: 'from-white via-emerald-50/30 to-white' },
    sky: { tile: 'from-sky-100 to-cyan-100 text-sky-700', card: 'from-white via-sky-50/30 to-white' },
    rose: { tile: 'from-rose-100 to-pink-100 text-rose-700', card: 'from-white via-rose-50/30 to-white' },
    violet: { tile: 'from-violet-100 to-fuchsia-100 text-violet-700', card: 'from-white via-violet-50/30 to-white' },
  };
  const a = accents[accent] || accents.emerald;
  return (
    <section data-testid={`profile-section-${id}`} className="hl-fade-up">
      <div className="flex items-center gap-2.5 mb-3 px-1">
        <span className={`inline-flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-br ${a.tile} shadow-sm ring-1 ring-white`}>
          {Icon && <Icon className="h-4 w-4" />}
        </span>
        <div className="min-w-0">
          {eyebrow && (
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-400">
              {eyebrow}
            </p>
          )}
          <h2 className="text-base sm:text-lg font-bold text-gray-900 leading-tight">{title}</h2>
        </div>
      </div>
      <Card className={`border border-gray-100/80 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.08)] rounded-3xl overflow-hidden bg-gradient-to-br ${a.card}`}>
        <CardContent className="p-5 sm:p-6 space-y-5">{children}</CardContent>
      </Card>
    </section>
  );
};

const StatChip = ({ label, value, icon: Icon, accent = 'text-gray-700', tone = 'gray', testid }) => {
  // Each tone gets a soft tinted background so the 4-up grid scans quickly.
  const tones = {
    emerald: 'bg-gradient-to-br from-emerald-50 to-emerald-50/30 border-emerald-100/70',
    violet: 'bg-gradient-to-br from-violet-50 to-violet-50/30 border-violet-100/70',
    amber: 'bg-gradient-to-br from-amber-50 to-amber-50/30 border-amber-100/70',
    sky: 'bg-gradient-to-br from-sky-50 to-sky-50/30 border-sky-100/70',
    orange: 'bg-gradient-to-br from-orange-50 to-orange-50/30 border-orange-100/70',
    rose: 'bg-gradient-to-br from-rose-50 to-rose-50/30 border-rose-100/70',
    gray: 'bg-white/80 border-gray-100',
  };
  return (
    <div
      className={`rounded-2xl border px-3.5 py-3 shadow-sm backdrop-blur-sm ${tones[tone] || tones.gray}`}
      data-testid={testid}
    >
      <div className={`inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider ${accent}`}>
        {Icon && <Icon className="h-3 w-3" />} {label}
      </div>
      <p className="mt-1 text-base sm:text-lg font-bold text-gray-900 tabular-nums leading-tight">{value}</p>
    </div>
  );
};

const TagInput = ({ label, items, value, onValueChange, onAdd, onRemove, placeholder, testIdRoot, badgeVariant = 'secondary', emptyLabel = 'No entries yet.' }) => (
  <div className="space-y-2.5">
    <Label className="text-sm font-semibold text-gray-800">{label}</Label>
    <div className="flex gap-2">
      <Input
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        placeholder={placeholder}
        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), onAdd())}
        className={FIELD_CLASS}
        data-testid={`${testIdRoot}-input`}
      />
      <Button
        type="button"
        onClick={onAdd}
        size="sm"
        className="h-12 px-4 rounded-2xl bg-emerald-600 hover:bg-emerald-700 shadow-[0_4px_12px_-4px_rgba(16,185,129,0.5)]"
        data-testid={`${testIdRoot}-add-btn`}
      >
        <Plus className="h-4 w-4" />
      </Button>
    </div>
    {items.length > 0 ? (
      <div className="flex flex-wrap gap-2" data-testid={`${testIdRoot}-list`}>
        {items.map((it, i) => (
          <Badge
            key={`${testIdRoot}-${it}-${i}`}
            variant={badgeVariant}
            className="flex items-center gap-1 rounded-full px-3 py-1.5 text-xs shadow-sm"
          >
            {it}
            <button
              type="button"
              onClick={() => onRemove(i)}
              className="ml-1 opacity-70 hover:opacity-100 motion-safe:transition-opacity"
              aria-label={`Remove ${it}`}
              data-testid={`${testIdRoot}-remove-${i}`}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
      </div>
    ) : (
      <p className="text-xs text-gray-400 italic">{emptyLabel}</p>
    )}
  </div>
);

const Profile = () => {
  const navigate = useNavigate();
  const { user, updateProfile } = useAuth();
  const { t, currentLanguage } = useLanguage();

  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  const [formData, setFormData] = useState({
    name: user?.name || '',
    date_of_birth: user?.date_of_birth || '',
    gender: user?.gender || '',
    height: user?.height || '',
    weight: user?.weight || '',
    medical_conditions: user?.medical_conditions || [],
    allergies: user?.allergies || [],
    medications: user?.medications || [],
    emergency_contact: user?.emergency_contact || '',
  });
  const [newCondition, setNewCondition] = useState('');
  const [newAllergy, setNewAllergy] = useState('');
  const [newMedication, setNewMedication] = useState('');

  // Sync formData when user updates from server (post-save).
  useEffect(() => {
    setFormData({
      name: user?.name || '',
      date_of_birth: user?.date_of_birth || '',
      gender: user?.gender || '',
      height: user?.height || '',
      weight: user?.weight || '',
      medical_conditions: user?.medical_conditions || [],
      allergies: user?.allergies || [],
      medications: user?.medications || [],
      emergency_contact: user?.emergency_contact || '',
    });
  }, [user]);

  // Email nudges
  const [nudgeOptIn, setNudgeOptIn] = useState(user?.nudge_emails_opt_in ?? true);
  const [nudgeSaving, setNudgeSaving] = useState(false);
  useEffect(() => {
    setNudgeOptIn(user?.nudge_emails_opt_in ?? true);
  }, [user?.nudge_emails_opt_in]);

  const handleNudgeToggle = async (next) => {
    setNudgeSaving(true);
    setNudgeOptIn(next);
    try {
      await healthApi.updateNudgeOptIn(next);
      toast.success(next ? 'Email nudges resumed' : 'Email nudges paused');
    } catch (err) {
      setNudgeOptIn(!next);
      toast.error(t('profileDeep.profileUpdateError'));
    } finally {
      setNudgeSaving(false);
    }
  };

  // Health summary (lightweight: uses what we already have on `user`)
  const [tracker, setTracker] = useState(null);
  const [reminders, setReminders] = useState(null);
  useEffect(() => {
    let cancelled = false;
    healthApi.getDailyTracker().then((res) => {
      if (!cancelled) setTracker(res?.data?.tracker || null);
    }).catch(() => {});
    // Reminders are loaded separately so a reminders-API failure never
    // blanks the rest of the page.
    (async () => {
      try {
        const { default: axios } = await import('axios');
        const API = process.env.REACT_APP_BACKEND_URL;
        const res = await axios.get(`${API}/api/health-reminders`, { withCredentials: true });
        if (!cancelled) {
          // Endpoint returns either a bare list or {reminders: [...]}
          const list = Array.isArray(res?.data) ? res.data : (res?.data?.reminders || []);
          setReminders(list);
        }
      } catch {
        if (!cancelled) setReminders([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const remindersCount = useMemo(() => {
    if (!Array.isArray(reminders)) return null;
    // Active = not yet completed. We don't filter on date here — the user
    // may want to see overdue items too, which still count as "active".
    return reminders.filter((r) => r?.completed !== true).length;
  }, [reminders]);

  // Cross-tab coach sync — when another tab updates the cached pick or
  // a coach signal, bump this state so our useMemo re-evaluates and adopts
  // the same label. Keeps multiple HealthLoop tabs visually consistent
  // without aggressive polling.
  const [sessionTick, setSessionTick] = useState(0);
  useEffect(() => {
    const onStorage = (e) => {
      if (!e?.key) return;
      // Listen for both the session pick AND the underlying signals key
      // so an engagement in one tab updates the chip in another tab.
      if (
        e.key.startsWith('hl_coach_session_v1_') ||
        e.key.startsWith('hl_coach_signals_v1_')
      ) {
        setSessionTick((t) => t + 1);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // ---- Next agenda derivation ----
  // Compose a single, calmly-worded "what's next" line from the reminder
  // list + today's tracker state. Health-critical modules (maternal /
  // medication keywords) outrank generic ones; today's items outrank
  // tomorrow's; only the SINGLE most relevant item surfaces — never two.
  //
  // IMPORTANT: We must NOT evaluate this until BOTH `tracker` and
  // `reminders` have loaded — otherwise the chip will briefly enter the
  // coach branch (because reminders=null is treated as 'no reminders'),
  // poisoning the localStorage signal layer with phantom 'shown' counts
  // for a label the user never actually saw. Returning a quiet
  // placeholder with coach=false prevents the shown-recorder useEffect
  // from running prematurely.
  const nextAgenda = useMemo(() => {
    if (reminders === null || tracker === null) {
      return { label: 'Looking ahead…', tone: 'gray', testid: 'agenda-loading', coach: false };
    }

    // ---- Context fingerprint (drives session-stable selection) ----
    // We hash the variables that SHOULD invalidate a cached pick:
    //   - bucket: which time-of-day bucket we're in
    //   - pct: whether the user has crossed 100% completion (for coach mode)
    //   - upcoming reminder ids + completed flags (sorted for stability)
    // Anything not in this fingerprint won't move the chip — including
    // re-renders, micro data jitter, or cross-tab updates that have the
    // same context.
    const todayISO_fp = new Date().toISOString().slice(0, 10);
    const hour_fp = new Date().getHours();
    const bucket_fp = hour_fp < 11 ? 'm' : hour_fp < 14 ? 'mi' : hour_fp < 17 ? 'a' : hour_fp < 21 ? 'e' : 'n';
    const upcoming_fp = (reminders || [])
      .filter((r) => r && r.completed !== true && typeof r.date === 'string' && r.date >= todayISO_fp)
      .map((r) => `${r.id}:${r.module || ''}:${r.date}:${r.time || ''}`)
      .sort()
      .join('|');
    const pct_fp = (tracker?.completion_percentage ?? 0) >= 100 ? 'c' : 'p';
    // Breathing-room flag: when true, the chip will show a quiet
    // acknowledgment instead of an optimized pick. Encoded in the
    // fingerprint so the cache invalidates naturally when the 45-min
    // window opens AND when it closes.
    const breathing_fp = isPostEngagementWindow(user?.id) ? 'b' : 'a';
    const fingerprint = `${bucket_fp}|${pct_fp}|${breathing_fp}|${upcoming_fp}|${sessionTick}`;

    // Try the session cache first — if context hasn't materially changed
    // and the cached pick isn't expired (4h TTL), reuse it. This is what
    // makes the chip feel dependable: same label across renders, refreshes,
    // and other tabs until something real moves.
    const cached = loadCoachSession(user?.id);
    if (cached && cached.fingerprint === fingerprint && cached.payload) {
      return cached.payload;
    }

    // Inner derivation — produces a fresh pick from current context.
    // Wrapped in a closure so the single fingerprint+save step at the
    // end doesn't have to be repeated at every early return.
    const derive = () => {
      const todayISO = todayISO_fp;
      const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowISO = tomorrow.toISOString().slice(0, 10);

    // Health-critical signal — module-based, plus a few keyword hints in
    // the title (medication, prenatal, scan, blood pressure). We keep this
    // list intentionally short to avoid false positives.
    const isCritical = (r) => {
      if (!r) return false;
      const m = (r.module || '').toLowerCase();
      if (m === 'maternal' || m === 'medication') return true;
      const t = (r.title || '').toLowerCase();
      return /\b(med|medication|dose|prenatal|scan|antenatal|insulin|blood pressure|bp)\b/.test(t);
    };

    const upcoming = (reminders || [])
      .filter((r) => r && r.completed !== true && typeof r.date === 'string')
      .filter((r) => r.date >= todayISO)
      .map((r) => ({
        ...r,
        _critical: isCritical(r),
      }));

    if (upcoming.length === 0) {
      // No urgent items. Three soft branches in priority order:
      //   (a) user hasn't checked in yet today → nudge them gently to it
      //   (b) user is in the post-engagement breathing window → quiet
      //       acknowledgment, NO further suggestion. Interaction matters
      //       more than impressions; we step back for ~45 minutes after
      //       a tap so the chip never feels like a recommender pushing
      //       the next thing.
      //   (c) user is fully caught up → switch into micro-coach mode and
      //       offer a single 3-5 word supportive next step. Never pushy,
      //       never gamified — these are options, not tasks.
      const pct = tracker?.completion_percentage ?? 0;
      const hour = new Date().getHours();

      if (pct < 100) {
        if (hour >= 18) return { label: t('profileDeep.checkinDueTonight'), tone: 'sky', testid: 'agenda-checkin-tonight' };
        return { label: t('profileDeep.checkinDueToday'), tone: 'sky', testid: 'agenda-checkin-today' };
      }

      // Breathing room: deliberately non-coach (coach: false) so the
      // chip is NOT tappable, NO 'shown' signal is recorded, and no
      // navigation happens. Pure ambient acknowledgment. Selection is
      // deterministic by day-of-year so the line itself stays calm
      // and consistent.
      if (isPostEngagementWindow(user?.id)) {
        const breathingLines = [
          'Nice — see you later',
          'Take your time',
          'Glad you took that',
          'Quiet for a bit',
        ];
        const startBR = new Date(new Date().getFullYear(), 0, 0);
        const dayBR = Math.floor((Date.now() - startBR.getTime()) / 86400000);
        return {
          label: breathingLines[dayBR % breathingLines.length],
          tone: 'gray',
          testid: 'agenda-coach-breathing',
          coach: false,
        };
      }

      // ---- Micro-coach (caught up & no reminders) ----
      // Suggestion library is grouped by time-of-day so the feeling
      // matches the moment. Within each bucket we add a small variety of
      // body / mind / mood prompts, plus a quiet acknowledgment so quiet
      // days never feel like dead air.
      const morning = [
        '2-minute stretch?',
        'Glass of water?',
        '3 deep breaths',
        'Sunlight • a few minutes',
      ];
      const midday = [
        'Hydration check',
        '1-minute pause',
        'Small walk after lunch?',
        'Mood reflection available',
      ];
      const afternoon = [
        '5-min movement break',
        'Glass of water?',
        'Breathing reset • 1 min',
        'A gentle stretch',
      ];
      const evening = [
        'Small walk tonight?',
        'Wind-down moment',
        'Gratitude • one thing',
        '4-7-8 breath, 1 min',
      ];
      const night = [
        'Sleep window approaching',
        'Screens down soon?',
        'Wind-down moment',
        'Slow exhale, twice',
      ];

      // Quiet acknowledgments — picked occasionally to break the pattern
      // and reward consistency without gamifying it.
      const acknowledgments = [
        "You've shown up today",
        'Quiet day — that\'s okay',
        'Steady week so far',
      ];

      let bucket;
      if (hour < 11) bucket = morning;
      else if (hour < 14) bucket = midday;
      else if (hour < 17) bucket = afternoon;
      else if (hour < 21) bucket = evening;
      else bucket = night;

      // Deterministic rotation per (calendar day, bucket) — kept as the
      // fallback when our quiet signals can't pick anything (everything
      // recent or no history yet).
      const start = new Date(new Date().getFullYear(), 0, 0);
      const diff = Date.now() - start.getTime();
      const dayOfYear = Math.floor(diff / 86400000);

      // Once-per-week cadence: surface an acknowledgment instead of an
      // action prompt, so the chip occasionally just says "thanks for
      // showing up". Day-of-year mod 7 picks the day of the week.
      const isAckDay = (dayOfYear % 7) === 0;
      const ackPick = acknowledgments[dayOfYear % acknowledgments.length];

      // Streak-aware acknowledgment: if the user has a 7+ day streak,
      // override with a warm 'consistent this week' line on Sundays
      // (or whenever isAckDay fires) so it doesn't feel random.
      const streak = tracker?.current_streak ?? 0;
      const consistentLabel = streak >= 7
        ? "You've been consistent this week"
        : ackPick;

      if (isAckDay) {
        return {
          label: consistentLabel,
          tone: 'emerald',
          testid: 'agenda-coach-ack',
          coach: true,
        };
      }

      // ---- Quiet adaptation (localStorage signals) ----
      // For each candidate compute a soft score from past shown/engaged
      // counts. Hard cooldown filters anything shown in last 6h.
      const signals = loadCoachSignals(user?.id);
      const scored = bucket
        .map((label) => ({ label, s: scoreCoach(signals, label) }))
        .filter((c) => c.s !== -Infinity);

      let label;
      if (scored.length === 0) {
        // Everything in cooldown → fall back to the deterministic rotation.
        const bucketIdx = bucket === morning ? 0 : bucket === midday ? 1 : bucket === afternoon ? 2 : bucket === evening ? 3 : 4;
        label = bucket[(dayOfYear + bucketIdx) % bucket.length];
      } else {
        // Sort by score desc; within a tie, keep the deterministic order
        // by day-of-year to stay stable across same-window refreshes.
        scored.sort((a, b) => b.s - a.s);
        const topScore = scored[0].s;
        // Diversity guard: if multiple share the top score, rotate by day.
        const top = scored.filter((c) => c.s === topScore);
        label = top[dayOfYear % top.length].label;
      }

      return {
        label,
        tone: 'emerald',
        testid: 'agenda-coach-suggestion',
        coach: true,
      };
    }

    // Sort: critical first, then by date asc, then by time asc (no time = end of day).
    upcoming.sort((a, b) => {
      if (a._critical !== b._critical) return a._critical ? -1 : 1;
      if (a.date !== b.date) return a.date < b.date ? -1 : 1;
      const at = a.time || '23:59';
      const bt = b.time || '23:59';
      return at < bt ? -1 : at > bt ? 1 : 0;
    });

    const top = upcoming[0];
    // Format the time portion in a calm 12-hour shape (no leading zero,
    // lowercase am/pm) — easier on the eye than a strict 24h clock.
    const formatTime = (t) => {
      if (!t || !/^\d{2}:\d{2}$/.test(t)) return null;
      const [hStr, mStr] = t.split(':');
      let h = parseInt(hStr, 10);
      const m = parseInt(mStr, 10);
      const suffix = h >= 12 ? 'PM' : 'AM';
      h = h % 12;
      if (h === 0) h = 12;
      return m === 0 ? `${h}:00 ${suffix}` : `${h}:${String(m).padStart(2, '0')} ${suffix}`;
    };

    // Date-relative phrase: "Today" / "Tonight" (today + 18h+) / "Tomorrow"
    // / weekday short name (e.g. "Fri") for anything 2–6 days away
    // / full short date for anything beyond.
    const dayPhrase = (() => {
      if (top.date === todayISO) {
        const t = top.time;
        if (t && /^\d{2}:\d{2}$/.test(t) && parseInt(t.slice(0, 2), 10) >= 18) {
          return 'Tonight';
        }
        return 'Today';
      }
      if (top.date === tomorrowISO) return 'Tomorrow';
      try {
        const d = new Date(`${top.date}T00:00:00`);
        const diffDays = Math.round((d.getTime() - Date.now()) / 86400000);
        if (diffDays >= 2 && diffDays <= 6) {
          return d.toLocaleDateString(undefined, { weekday: 'short' });
        }
        return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      } catch {
        return 'Soon';
      }
    })();

    // Compose the one-line label. Time wins when present and same-day —
    // "Tonight • 8:00 PM" reads cleanest. Otherwise pair date with title.
    const time = formatTime(top.time);
    const title = (top.title || '').trim();
    let label;
    if (time && (top.date === todayISO)) {
      label = `${dayPhrase} • ${time}`;
    } else if (title) {
      // Truncate long titles so the chip stays one-line on narrow phones.
      const short = title.length > 22 ? `${title.slice(0, 22).trim()}…` : title;
      label = `${dayPhrase} • ${short}`;
    } else {
      label = dayPhrase;
    }

    return {
      label,
      tone: top._critical ? 'rose' : (top.date === todayISO ? 'sky' : 'gray'),
      testid: `agenda-reminder-${top.id || ''}`,
    };
    }; // end derive()

    const picked = derive();
    // Cache the pick so subsequent renders / refreshes / other tabs reuse
    // it as long as the fingerprint matches.
    saveCoachSession(user?.id, { fingerprint, payload: picked });
    return picked;
  }, [reminders, tracker, user?.id, sessionTick]);

  // Record "shown" once whenever we're in coach mode and the picked label changes.
  // We dedupe per-session via a ref so that transient label changes during
  // initial data load (tracker arrives before reminders, etc.) don't get
  // double-counted. A given suggestion is only recorded once per page mount;
  // the next visit (fresh mount) will record again, which is the right
  // granularity for cooldown / fade signals.
  const shownThisMount = useRef(new Set());
  useEffect(() => {
    if (
      nextAgenda?.coach &&
      nextAgenda?.label &&
      user?.id &&
      !shownThisMount.current.has(nextAgenda.label)
    ) {
      shownThisMount.current.add(nextAgenda.label);
      recordCoachShown(user.id, nextAgenda.label);
      queueCoachSync();
    }
    // queueCoachSync is intentionally referenced inline — it doesn't need
    // to be a dep because it reads from refs/storage directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextAgenda?.coach, nextAgenda?.label, user?.id]);

  // Tap handler — chip is interactive only in coach mode, where tapping it
  // is the gentle "yes, I'd like to do this" signal. Quiet engagement.
  const handleAgendaTap = () => {
    if (nextAgenda?.coach && user?.id) {
      recordCoachEngaged(user.id, nextAgenda.label);
      // Engagement is a "meaningful user interaction" — invalidate the
      // session pick so the next time the chip surfaces (e.g. after
      // returning from check-in), it can pick a fresh label that
      // accounts for the just-recorded engagement signal.
      clearCoachSession(user.id);
      // Open the post-engagement breathing window. For ~45 minutes the
      // chip won't propose another optimized pick — interaction matters
      // more than impressions, and the user deserves a moment of quiet
      // after acting on a suggestion.
      recordEngagementMoment(user.id);
      queueCoachSync();
    }
    // Always navigate somewhere supportive — the daily check-in is the
    // single most-relevant follow-up surface for any of the prompts.
    navigate('/smart-check-in');
  };

  // ---- Optional cross-device coach continuity ----
  // We piggyback on `nextAgenda` changes (which only fire when shown/engaged
  // counters move) plus the engagement tap to push local signals upstream
  // when the user has opted in. The push is debounced to ~10s so a burst
  // of activity coalesces into a single PUT.
  const syncTimerRef = useRef(null);
  const queueCoachSync = () => {
    if (!user?.id) return;
    let optedIn = false;
    try {
      optedIn = window.localStorage?.getItem(`hl_coach_opted_in_v1_${user.id}`) === '1';
    } catch { /* empty */ }
    if (!optedIn) return;
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(async () => {
      try {
        const { default: axios } = await import('axios');
        const API = process.env.REACT_APP_BACKEND_URL;
        const signals = loadCoachSignals(user.id);
        await axios.put(
          `${API}/api/me/coach-preferences`,
          { opted_in: true, signals },
          { withCredentials: true },
        );
      } catch {
        // Best-effort. Local state is authoritative; sync failure never blocks UX.
      }
    }, 10000);
  };

  useEffect(() => {
    return () => {
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    };
  }, []);

  const completion = useMemo(() => computeCompletion(formData), [formData]);
  const completionMsg = completion >= 100
    ? t('profileDeep.completionComplete')
    : completion >= 70
      ? t('profileDeep.completionAlmostThere')
      : t('profileDeep.completionInProgress');

  const memberSince = useMemo(() => {
    if (!user?.created_at) return '—';
    try {
      const d = new Date(user.created_at);
      return d.toLocaleDateString(currentLanguage || undefined, { month: 'short', year: 'numeric' });
    } catch { return '—'; }
  }, [user?.created_at, currentLanguage]);

  // Relative "Last updated" label — e.g. "2 hours ago", "3 days ago".
  // Falls back to a date string for older edits.
  const lastUpdated = useMemo(() => {
    if (!user?.updated_at) return null;
    try {
      const d = new Date(user.updated_at);
      const diffMs = Date.now() - d.getTime();
      const m = Math.floor(diffMs / 60000);
      if (m < 1) return t('profileDeep.justNow');
      if (m < 60) return t(m === 1 ? 'profileDeep.minAgo' : 'profileDeep.minsAgo', { n: m });
      const h = Math.floor(m / 60);
      if (h < 24) return t(h === 1 ? 'profileDeep.hourAgo' : 'profileDeep.hoursAgo', { n: h });
      const days = Math.floor(h / 24);
      if (days < 7) return t(days === 1 ? 'profileDeep.dayAgo' : 'profileDeep.daysAgo', { n: days });
      return d.toLocaleDateString(currentLanguage || undefined, { month: 'short', day: 'numeric' });
    } catch { return null; }
  }, [user?.updated_at, t, currentLanguage]);

  // Stale-profile signal: if the user hasn't saved in 60+ days (or has
  // never saved), surface a gentle nudge to refresh. We don't nag users
  // who edited recently — `isStale` is false when `updated_at` is fresh.
  const isStale = useMemo(() => {
    if (!user?.updated_at) return true; // never saved
    try {
      const diffDays = (Date.now() - new Date(user.updated_at).getTime()) / 86400000;
      return diffDays > 60;
    } catch { return false; }
  }, [user?.updated_at]);

  const initials = useMemo(() => {
    const raw = (user?.name || '').trim();
    if (!raw) return user?.email?.[0]?.toUpperCase() || '?';
    const parts = raw.split(/\s+/).filter(Boolean);
    return ((parts[0]?.[0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase() || '?';
  }, [user?.name, user?.email]);

  const handleAdd = (key, valueState, setValueState) => {
    const v = valueState.trim();
    if (!v) return;
    setFormData((d) => ({ ...d, [key]: [...(d[key] || []), v] }));
    setValueState('');
  };
  const handleRemove = (key, idx) => {
    setFormData((d) => ({ ...d, [key]: d[key].filter((_, i) => i !== idx) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await updateProfile(formData);
    setLoading(false);
    if (result.success) {
      toast.success(t('messages.updated') || 'Profile saved — thanks for keeping things up to date.');
      setEditMode(false);
    } else {
      toast.error(result.error || 'Failed to update profile');
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 pb-32"
      data-testid="profile-page"
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 space-y-7 hl-stagger" data-testid="profile-page-container">
        <button
          onClick={() => navigate('/dashboard')}
          className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
          data-testid="profile-back-btn"
        >
          <ArrowLeft className="h-4 w-4" /> {t('profileDeep.backToDashboard')}
        </button>

        {/* Wellness sync status — subtle, only renders when there's actually
            something pending across check-ins / reminders / step tracker.
            Silent in the happy path so the Profile screen stays calm. */}
        <WellnessPendingBadge variant="panel" data-testid="profile-wellness-pending" />

        {/* PROFILE HEADER — avatar, name, member since, streak, edit btn */}
        <Card
          className="border border-emerald-100/70 shadow-[0_2px_4px_rgba(15,23,42,0.04),0_24px_48px_-24px_rgba(16,185,129,0.18)] rounded-3xl overflow-hidden bg-gradient-to-br from-emerald-50 via-white to-teal-50/60 hl-fade-up"
          data-testid="profile-header-card"
        >
          <CardContent className="p-5 sm:p-6">
            <div className="flex items-start gap-4">
              <div
                className="shrink-0 flex items-center justify-center w-[68px] h-[68px] rounded-2xl bg-gradient-to-br from-emerald-400 via-teal-500 to-emerald-600 text-white text-xl font-bold shadow-[0_8px_24px_-8px_rgba(16,185,129,0.6)] ring-2 ring-white"
                data-testid="profile-avatar"
              >
                {initials}
              </div>
              <div className="flex-1 min-w-0">
                <h1
                  className="text-xl sm:text-2xl font-bold text-gray-900 truncate leading-tight"
                  data-testid="profile-name"
                >
                  {user?.name || t('profileDeep.healthLoopUser')}
                </h1>
                <p className="text-xs text-gray-500 truncate" data-testid="profile-email">
                  {user?.email}
                </p>
                {lastUpdated && (
                  <p
                    className="mt-1 text-[11px] text-gray-400"
                    data-testid="profile-last-updated"
                  >
                    {t('profileDeep.updatedLabel')} {lastUpdated}
                  </p>
                )}
                <div className="mt-3 grid grid-cols-2 gap-2.5 sm:max-w-sm">
                  <StatChip
                    label={t('profileDeep.memberSince')}
                    value={memberSince}
                    icon={CalendarClock}
                    accent="text-gray-600"
                    tone="gray"
                  />
                  <StatChip
                    label={t('profileDeep.streak')}
                    value={t('profileDeep.streakDays', { n: tracker?.current_streak ?? 0 })}
                    icon={Flame}
                    accent="text-orange-600"
                    tone="orange"
                  />
                </div>
              </div>
            </div>

            <div className="mt-5">
              <Button
                type="button"
                onClick={() => setEditMode((v) => !v)}
                className={`w-full h-12 rounded-2xl shadow-sm font-semibold text-[15px] motion-safe:transition-all ${
                  editMode
                    ? 'bg-white text-emerald-700 border border-emerald-200 hover:bg-emerald-50'
                    : 'bg-gradient-to-br from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white shadow-[0_8px_20px_-8px_rgba(16,185,129,0.55)]'
                }`}
                data-testid="profile-edit-toggle-btn"
              >
                <Pencil className="h-4 w-4 mr-2" />
                {editMode ? t('profileDeep.cancelEditing') : t('profileDeep.editProfile')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stale-data nudge — surfaces when the user hasn't saved in
            60+ days, or has never saved. Hidden once they save. */}
        {isStale && (
          <Card
            className="border border-amber-200/70 bg-amber-50/70 shadow-sm rounded-2xl"
            data-testid="profile-stale-banner"
          >
            <CardContent className="p-4 flex items-start gap-3">
              <span className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg bg-amber-100 text-amber-700">
                <CalendarClock className="h-4 w-4" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-amber-900">
                  {t('profileDeep.profileMayBeOutOfDate')}
                </p>
                <p className="text-xs text-amber-800/90 mt-0.5">
                  {t('profileDeep.profileMayBeOutOfDateDesc')}
                </p>
                <button
                  type="button"
                  onClick={() => setEditMode(true)}
                  className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-amber-900 underline underline-offset-2 hover:text-amber-700"
                  data-testid="profile-stale-banner-edit-btn"
                >
                  <Pencil className="h-3 w-3" /> {t('profileDeep.updateNow')}
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* PROFILE COMPLETION */}
        <Card
          className="border border-gray-100/80 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.08)] rounded-3xl bg-gradient-to-br from-white via-emerald-50/20 to-white hl-fade-up"
          data-testid="profile-completion-card"
        >
          <CardContent className="p-5 sm:p-6">
            <div className="flex items-center justify-between mb-2.5">
              <p className="text-[11px] font-semibold text-emerald-700 uppercase tracking-widest flex items-center gap-1.5">
                <Sparkles className="h-3 w-3" /> {t('profileDeep.profileCompletion')}
              </p>
              <span
                className="text-base font-bold text-gray-900 tabular-nums"
                data-testid="profile-completion-pct"
              >
                {completion}%
              </span>
            </div>
            <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden shadow-inner">
              <div
                className="h-full bg-gradient-to-r from-emerald-400 via-teal-400 to-emerald-500 motion-safe:transition-[width] duration-1000 ease-out shadow-[0_0_12px_rgba(52,211,153,0.4)]"
                style={{ width: `${completion}%` }}
                data-testid="profile-completion-bar"
              />
            </div>
            <p className="mt-3 text-xs sm:text-sm text-gray-600 leading-relaxed">{completionMsg}</p>
          </CardContent>
        </Card>

        {/* HEALTH SUMMARY */}
        <SectionCard
          id="health-summary"
          title={t('profileDeep.healthSummary')}
          icon={Activity}
          accent="emerald"
        >
          <div className="grid grid-cols-2 gap-3">
            <StatChip
              label={t('profileDeep.healthScore')}
              value={`${tracker?.health_score ?? 0}/100`}
              icon={Activity}
              accent="text-emerald-700"
              tone="emerald"
            />
            <StatChip
              label={t('profileDeep.remindersActive')}
              value={remindersCount ?? '—'}
              icon={Bell}
              accent="text-violet-700"
              tone="violet"
            />
            <StatChip
              label={t('profileDeep.conditionsTracked')}
              value={(formData.medical_conditions || []).length}
              icon={AlertTriangle}
              accent="text-amber-700"
              tone="amber"
            />
            {nextAgenda?.coach ? (
              <button
                type="button"
                onClick={handleAgendaTap}
                className="text-left motion-safe:transition-transform active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 rounded-2xl hl-press"
                data-testid="profile-next-agenda-tap"
                aria-label={`${nextAgenda.label} — tap for a quick check-in`}
              >
                <StatChip
                  label={t('profileDeep.nextAgenda')}
                  value={nextAgenda.label}
                  icon={CalendarClock}
                  accent="text-emerald-700"
                  tone={nextAgenda.tone}
                  testid={`profile-next-agenda-${nextAgenda.testid}`}
                />
              </button>
            ) : (
              <StatChip
                label={t('profileDeep.nextAgenda')}
                value={nextAgenda.label}
                icon={CalendarClock}
                accent={
                  nextAgenda.tone === 'rose'
                    ? 'text-rose-700'
                    : nextAgenda.tone === 'sky'
                    ? 'text-sky-700'
                    : 'text-gray-600'
                }
                tone={nextAgenda.tone}
                testid={`profile-next-agenda-${nextAgenda.testid}`}
              />
            )}
          </div>
        </SectionCard>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* ACCOUNT — lightweight section header (2026-05-23 IA polish).
              Groups Personal Details + Health Information + Account Settings
              visually without restructuring the underlying cards. */}
          <h2
            className="px-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-400"
            data-testid="profile-section-header-account"
          >
            {t('profileDeep.sectionAccount', 'Account')}
          </h2>

          {/* PERSONAL DETAILS */}
          <SectionCard
            id="personal-details"
            title={t('profileDeep.personalDetails')}
            icon={Heart}
            accent="rose"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="name" className="text-sm font-semibold text-gray-800">{t('profileDeep.fullName')}</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  disabled={!editMode}
                  className={FIELD_CLASS}
                  data-testid="profile-input-name"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-sm font-semibold text-gray-800">{t('profileDeep.email')}</Label>
                <Input
                  id="email"
                  value={user?.email || ''}
                  disabled
                  className={FIELD_CLASS}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="dob" className="text-sm font-semibold text-gray-800">{t('profileDeep.dob')}</Label>
                <Input
                  id="dob"
                  type="date"
                  value={formData.date_of_birth || ''}
                  onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                  disabled={!editMode}
                  className={FIELD_CLASS}
                  data-testid="profile-input-dob"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="gender" className="text-sm font-semibold text-gray-800">{t('profileDeep.gender')}</Label>
                <Input
                  id="gender"
                  placeholder={t('profileDeep.genderPlaceholder')}
                  value={formData.gender || ''}
                  onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                  disabled={!editMode}
                  className={FIELD_CLASS}
                  data-testid="profile-input-gender"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="height" className="text-sm font-semibold text-gray-800">{t('profileDeep.height')}</Label>
                <Input
                  id="height"
                  type="number"
                  step="0.1"
                  placeholder={t('profileDeep.heightPlaceholder')}
                  value={formData.height || ''}
                  onChange={(e) => setFormData({ ...formData, height: parseFloat(e.target.value) || '' })}
                  disabled={!editMode}
                  className={FIELD_CLASS}
                  data-testid="profile-input-height"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="weight" className="text-sm font-semibold text-gray-800">{t('profileDeep.weight')}</Label>
                <Input
                  id="weight"
                  type="number"
                  step="0.1"
                  placeholder={t('profileDeep.weightPlaceholder')}
                  value={formData.weight || ''}
                  onChange={(e) => setFormData({ ...formData, weight: parseFloat(e.target.value) || '' })}
                  disabled={!editMode}
                  className={FIELD_CLASS}
                  data-testid="profile-input-weight"
                />
              </div>
            </div>
          </SectionCard>

          {/* HEALTH INFORMATION */}
          <SectionCard
            id="health-information"
            title={t('profileDeep.healthInformation')}
            icon={Pill}
            accent="sky"
          >
            {/* Share with a clinician — privacy-first, opens a consent modal */}
            <div
              className="flex items-center justify-between gap-3 rounded-xl border border-emerald-100 bg-emerald-50/50 p-3"
              data-testid="profile-share-clinician-card"
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg bg-white text-emerald-700 shadow-sm">
                  <Stethoscope className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900">
                    {t('profileDeep.shareWithClinician')}
                  </p>
                  <p className="text-xs text-gray-600 truncate">
                    {t('profileDeep.shareWithClinicianDesc')}
                  </p>
                </div>
              </div>
              <Button
                type="button"
                size="sm"
                onClick={() => setShowShareModal(true)}
                className="bg-emerald-600 hover:bg-emerald-700 h-9 rounded-lg shadow-sm"
                data-testid="profile-share-clinician-btn"
              >
                {t('profileDeep.share')}
              </Button>
            </div>

            <fieldset disabled={!editMode} className="space-y-5">
              <TagInput
                label={t('profileDeep.allergies')}
                items={formData.allergies}
                value={newAllergy}
                onValueChange={setNewAllergy}
                onAdd={() => handleAdd('allergies', newAllergy, setNewAllergy)}
                onRemove={(i) => handleRemove('allergies', i)}
                placeholder={t('profileDeep.allergiesPlaceholder')}
                testIdRoot="profile-allergies"
                badgeVariant="destructive"
                emptyLabel={t('profileDeep.noEntries')}
              />
              <TagInput
                label={t('profileDeep.medicalConditions')}
                items={formData.medical_conditions}
                value={newCondition}
                onValueChange={setNewCondition}
                onAdd={() => handleAdd('medical_conditions', newCondition, setNewCondition)}
                onRemove={(i) => handleRemove('medical_conditions', i)}
                placeholder={t('profileDeep.conditionsPlaceholder')}
                testIdRoot="profile-conditions"
                badgeVariant="secondary"
                emptyLabel={t('profileDeep.noEntries')}
              />
              <TagInput
                label={t('profileDeep.medications')}
                items={formData.medications}
                value={newMedication}
                onValueChange={setNewMedication}
                onAdd={() => handleAdd('medications', newMedication, setNewMedication)}
                onRemove={(i) => handleRemove('medications', i)}
                placeholder={t('profileDeep.medicationsPlaceholder')}
                testIdRoot="profile-medications"
                badgeVariant="outline"
                emptyLabel={t('profileDeep.noEntries')}
              />
              <div className="space-y-1.5">
                <Label htmlFor="emergency" className="text-sm font-semibold text-gray-800">
                  {t('profileDeep.emergencyContact')} <span className="font-normal text-gray-500">{t('profileDeep.emergencyOptional')}</span>
                </Label>
                <Input
                  id="emergency"
                  placeholder={t('profileDeep.emergencyPlaceholder')}
                  value={formData.emergency_contact || ''}
                  onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })}
                  className={FIELD_CLASS}
                  data-testid="profile-input-emergency"
                />
                <p className="text-xs text-gray-500 italic">
                  {t('profileDeep.emergencyNote')}
                </p>
              </div>
            </fieldset>
          </SectionCard>

          {editMode && (
            <Button
              type="submit"
              size="lg"
              disabled={loading}
              className="w-full h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-semibold shadow-[0_8px_20px_-8px_rgba(16,185,129,0.55)] motion-safe:transition-all"
              data-testid="profile-save-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              {loading ? t('profileDeep.saving') : t('profileDeep.saveChanges')}
            </Button>
          )}
        </form>

        {/* ACCOUNT SETTINGS */}
        <SectionCard
          id="account-settings"
          title={t('profileDeep.accountSettings')}
          icon={ShieldCheck}
          accent="violet"
        >
          {/* Notifications / reminders */}
          <Link to="/settings/reminders" className="block">
            <div className="flex items-center justify-between gap-3 rounded-2xl border border-gray-100 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:shadow-md hover:border-violet-200 motion-safe:transition-all">
              <div className="flex items-center gap-3 min-w-0">
                <span className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-violet-100 to-fuchsia-100 text-violet-700 shadow-sm">
                  <Bell className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900">{t('profileDeep.notificationsReminders')}</p>
                  <p className="text-xs text-gray-500 truncate">{t('profileDeep.notificationsRemindersDesc')}</p>
                </div>
              </div>
              <ArrowLeft className="h-4 w-4 text-gray-400 transform rotate-180" />
            </div>
          </Link>

          {/* Email nudges toggle */}
          <div
            className="flex items-center justify-between gap-3 rounded-2xl border border-gray-100 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
            data-testid="profile-email-nudges-card"
          >
            <div className="flex items-center gap-3 min-w-0">
              <span className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100 text-emerald-700 shadow-sm">
                <Mail className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900">{t('profileDeep.emailNudges')}</p>
                <p className="text-xs text-gray-500 truncate">
                  {t('profileDeep.emailNudgesDesc')} · {nudgeOptIn ? t('profileDeep.on') : t('profileDeep.paused')}
                </p>
              </div>
            </div>
            <Switch
              checked={nudgeOptIn}
              onCheckedChange={handleNudgeToggle}
              disabled={nudgeSaving}
              data-testid="profile-email-nudges-toggle"
            />
          </div>

          {/* Coach continuity — opt-in cross-device sync */}
          {user?.id && (
            <CoachContinuityToggle
              userId={user.id}
              getLocalSignals={() => loadCoachSignals(user.id)}
              applyServerSignals={(signals) => mergeCoachSignals(user.id, signals)}
            />
          )}

          {/* Privacy & Security */}
          <div
            className="rounded-2xl border border-gray-100 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
            data-testid="profile-security-card"
          >
            <div className="flex items-center gap-3 mb-3">
              <span className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-sky-100 to-cyan-100 text-sky-700 shadow-sm">
                <Lock className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900">{t('profileDeep.privacySecurity')}</p>
                <p className="text-xs text-gray-500">{t('profileDeep.privacySecurityDesc')}</p>
              </div>
            </div>
            <ChangePassword />
          </div>

          {/* Preferences (language) */}
          <div
            className="rounded-2xl border border-gray-100 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
            data-testid="profile-preferences-card"
          >
            <div className="flex items-center gap-3 mb-3">
              <span className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-amber-100 to-orange-100 text-amber-700 shadow-sm">
                <Globe2 className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900">{t('profileDeep.preferencesTitle')}</p>
                <p className="text-xs text-gray-500">{t('profileDeep.preferencesDesc')}</p>
              </div>
            </div>
            <LanguageSelector />
          </div>
        </SectionCard>

        {/* SUPPORT — lightweight section header */}
        <h2
          className="px-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-400"
          data-testid="profile-section-header-support"
        >
          {t('profileDeep.sectionSupport', 'Support')}
        </h2>

        {/* SUPPORT row — preserved from old layout */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Link to="/support-help" className="block">
            <Card className="border border-gray-100 shadow-sm rounded-2xl hover:shadow transition-shadow">
              <CardContent className="p-4 flex items-center gap-3">
                <span className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg bg-emerald-50 text-emerald-700">
                  <HelpCircle className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900">{t('profileDeep.supportHelp')}</p>
                  <p className="text-xs text-gray-500 truncate">{t('profileDeep.supportHelpDesc')}</p>
                </div>
              </CardContent>
            </Card>
          </Link>
          <button
            type="button"
            onClick={() => setShowFeedbackModal(true)}
            className="text-left"
            data-testid="profile-feedback-btn"
          >
            <Card className="border border-gray-100 shadow-sm rounded-2xl hover:shadow transition-shadow">
              <CardContent className="p-4 flex items-center gap-3">
                <span className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg bg-sky-50 text-sky-700">
                  <MessageSquare className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900">{t('profileDeep.giveFeedback')}</p>
                  <p className="text-xs text-gray-500 truncate">{t('profileDeep.giveFeedbackDesc')}</p>
                </div>
              </CardContent>
            </Card>
          </button>
          <button
            type="button"
            onClick={() => setShowReportModal(true)}
            className="text-left"
            data-testid="profile-report-btn"
          >
            <Card className="border border-gray-100 shadow-sm rounded-2xl hover:shadow transition-shadow">
              <CardContent className="p-4 flex items-center gap-3">
                <span className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg bg-rose-50 text-rose-700">
                  <Bug className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900">{t('profileDeep.reportProblem')}</p>
                  <p className="text-xs text-gray-500 truncate">{t('profileDeep.reportProblemDesc')}</p>
                </div>
              </CardContent>
            </Card>
          </button>
        </div>

        {/* ADMIN — only for admin role; lightweight section header */}
        {user?.role === 'admin' && (
          <>
            <h2
              className="px-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-400"
              data-testid="profile-section-header-admin"
            >
              {t('profileDeep.sectionAdmin', 'Admin')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Link to="/admin/insights" className="block">
                <Card className="border border-gray-100 shadow-sm rounded-2xl hover:shadow transition-shadow">
                  <CardContent className="p-4 flex items-center gap-3">
                    <span className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg bg-indigo-50 text-indigo-700">
                      <BarChart3 className="h-4 w-4" />
                    </span>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                        <span>{t('profileDeep.adminInsights')}</span>
                        <Badge className="bg-indigo-600 text-white text-[10px] py-0 px-1.5">{t('profileDeep.adminBadge')}</Badge>
                      </div>
                      <p className="text-xs text-gray-500 truncate">{t('profileDeep.adminInsightsDesc')}</p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </div>
          </>
        )}

        {/* ABOUT — slim, text-only block (Privacy / Terms / Version).
            Lightweight informational footer above AppFooter. */}
        <div className="pt-2" data-testid="profile-about-block">
          <h2 className="px-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-400 mb-2">
            {t('profileDeep.sectionAbout', 'About')}
          </h2>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-1 text-xs text-gray-600">
            <Link
              to="/privacy"
              className="hover:text-emerald-700 transition-colors"
              data-testid="profile-about-privacy-link"
            >
              {t('profileDeep.privacyPolicy', 'Privacy Policy')}
            </Link>
            <span className="text-gray-300" aria-hidden="true">·</span>
            <Link
              to="/privacy"
              className="hover:text-emerald-700 transition-colors"
              data-testid="profile-about-terms-link"
            >
              {t('profileDeep.terms', 'Terms')}
            </Link>
            <span className="text-gray-300" aria-hidden="true">·</span>
            <span className="text-gray-500" data-testid="profile-about-version">
              {t('profileDeep.appVersion', 'App version')} 0.1.0
            </span>
          </div>
        </div>
      </div>

      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        page="profile"
      />
      <ReportProblemModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        page="profile"
      />
      <ClinicianShareModal
        open={showShareModal}
        onClose={() => setShowShareModal(false)}
      />

      <AppFooter />
    </div>
  );
};

export default Profile;
